import { FC, useEffect, useState } from 'react';
import { Route, Routes, useLocation, useSearchParams } from 'react-router-dom';
import { AppReactContext, IAppContext } from 'src/AppContext';
import { withRouter, WithRouterProps } from 'src/helpers/RouterHelper';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { Dashboard, FilesPage, JobDetails, JobsPage } from 'src/pages';
import { authUserSelector, fetchUserEntitlements } from 'src/store/slices/authUser';
import { fetchInfo, infoSelector } from 'src/store/slices/info';
import { menuSelector, setMenu } from 'src/store/slices/menus';
import { fetchUsers } from 'src/store/slices/users';
import { UserEntitlements } from 'src/store/slices/users/types';
import SpinnerProvider from '../../context/SpinnerContext';
import ErrorBoundary from '../../helpers/ErrorBoundary';
import Logout from '../Auth/Logout';
import CsHeader from '../CsHeader/CsHeader';
import navbarConfig from '../Navbar/config';
import Navbar from '../Navbar/Index';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import { MenuItem } from '../Navbar/types';

interface AppProps extends WithRouterProps {
  context: IAppContext;
}

interface PagesProps {
  isAuthenticated: boolean;
}

const EmptyPage = () => {
  return <div>EmptyPage</div>;
};

const Pages = (props: PagesProps) => {
  return (
    <div className="p-4 bg-grey-08 h-100 w-100">
      <Routes>
        {props.isAuthenticated && (
          <>
            <Route path="/" element={<Dashboard />} />
            <Route path="/files" element={<FilesPage />} />
            <Route path="/jobs" element={<JobsPage />} />
            <Route path="/jobs/:jobId" element={<JobDetails />} />
          </>
        )}

        {!props.isAuthenticated && <Route path="/" element={<EmptyPage />} />}
        <>
          <Route path="/logout" element={<Logout />} />
          <Route path="*" element={<NotFoundPage />} />
        </>
      </Routes>
    </div>
  );
};

const App: FC<AppProps> = ({ context }) => {
  const dispatch = useAppDispatch();
  const { productName, apiVersion } = useAppSelector(infoSelector);
  const [searchParams, setSearchParams] = useSearchParams();
  const customerId = searchParams.get('customerId') ?? '';
  const location = useLocation();
  const [previousCustomerId, setPreviousCustomerId] = useState<string>('');
  const authUser = useAppSelector(authUserSelector);
  const { menu } = useAppSelector(menuSelector);

  useEffect(() => {
    setPreviousCustomerId(customerId);
  }, []);

  useEffect(() => {
    if (!customerId && previousCustomerId) {
      setSearchParams({ customerId: previousCustomerId });
    }
  }, [location]);

  useEffect(() => {
    if (customerId || previousCustomerId) {
      sessionStorage.setItem('customerId', customerId || previousCustomerId);
    } else {
      sessionStorage.removeItem('customerId');
    }
  }, [customerId]);

  useEffect(() => {
    document.title = productName || 'Sphera Cloud';
  }, [productName]);

  const onFetchError = (infoResponse: any) => {
    window.location.href = infoResponse.payload.data.urls.find((url: any) => url.target === 'Landing').url;
  };

  useEffect(() => {
    dispatch(fetchInfo());
    dispatch(fetchUsers()).then((response: any) => {
      if (response.error) {
        dispatch(fetchInfo()).then(onFetchError);
      }
    });
    dispatch(fetchUserEntitlements()).then(({ payload }: { payload: any }) => {
      const MODULE_VALUE = "Collab";
      const entitlements = payload.data as UserEntitlements;
      const moduleName = entitlements.user?.modules?.find(module => module.value === MODULE_VALUE)?.name;
      const solutionFamily = entitlements?.productLines?.[1]?.solutionFamilies.find(solFam => solFam.group === moduleName);

      const initialMenu = [...navbarConfig.menus] as MenuItem[];
      if (solutionFamily?.group) initialMenu[0].label = solutionFamily?.group;
      initialMenu[0].avatar = solutionFamily?.shortcut;
      initialMenu[0].avatarColor = solutionFamily?.iconColor;
      dispatch(setMenu(initialMenu));
    });
  }, []);

  return (
    <AppReactContext.Provider value={context}>
      {authUser && menu && (
        <>
          <CsHeader apiVersion={apiVersion} />
          <Navbar context={context}>
            <ErrorBoundary>
              <div className="dm-drawer-content">
                <SpinnerProvider>
                  <Pages isAuthenticated />
                </SpinnerProvider>
              </div>
            </ErrorBoundary>
          </Navbar>
        </>
      )}
    </AppReactContext.Provider>
  );
};

export default withRouter(App);
